<script setup>
import { http } from "@/composable/httpService";
import { getRoles } from "@/composable/users/getRoles";
import { useAuthStore } from "@/stores";
import { useTeamProgressStore } from "@/stores/home/useTeamProgress.store";
import { ElSwitch } from "element-plus";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { computed, defineProps } from "vue";

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
    required: false,
  },
  showBorder: {
    type: Boolean,
    default: false,
  }
});

const authStore = useAuthStore();
const teamProgressStore = useTeamProgressStore();

const { user } = storeToRefs(authStore);

const loadingProgress = ref(false);
const isActingAsTeamLeader = ref(user.value?.is_team_leader);

const collapsed = computed(() => props.collapsed);
const isTeamLeader = computed(() => {
  const isLeader = getRoles().isTeamLeaderWithoutActingAsTeamLeader.value;

  return (
    isLeader &&
    (getRoles()?.isSuperAdmin?.value ||
      getRoles().isAccountManager?.value ||
      getRoles().isSpecificManager?.value ||
      getRoles().isMultiStoreManager?.value ||
      getRoles().isRegionManager?.value)
  );
});

const profilePicture = computed(() => user.value?.profile_picture);
const initials = computed(() => {
  if (user.value) {
    const names = user.value.name.split(" ");
    return names
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }
  return "";
});
const roles = computed(() => authStore.rolesFriendly?.[0] ?? "");

const switchAsTeamLeader = () => {
  loadingProgress.value = true;
  http()
    .put("v3/switch-role")
    .then(() => {
      teamProgressStore.handleFetchAssignedSummary();
      window.location.reload();
    })
    .catch((error) => {
      console.log(error);
    });
};
</script>
<template>
  <div
    v-bind="$attrs"
    class="flex flex-col gap-3 border-b border-untitled-gray-200 shrink-0"
    :class="{
      'p-4': !collapsed,
      'justify-center items-center ': collapsed,
      'border-r border-untitled-gray-100': showBorder
    }"
  >
    <div class="flex items-center gap-3">
      <div class="relative shrink-0">
        <div
          v-if="!profilePicture"
          class="rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 text-white flex items-center justify-center font-heading font-bold text-sm ring-2 ring-white shadow-sm"
          :class="{
            'w-11 h-11': !collapsed,
          }"
        >
          {{ initials }}
        </div>
        <div
          v-else
          class="rounded-full ring-2 ring-white bg-untitled-gray-200 shadow-sm overflow-hidden"
          :class="{
            'w-11 h-11': !collapsed,
          }"
        >
          <img
            :src="profilePicture"
            alt="Profile Picture"
            class="rounded-full object-cover ring-2 ring-white shadow-sm"
            :class="{
              'w-11 h-11': !collapsed,
              'w-10 h-10': collapsed,
            }"
          />
        </div>

        <span
          v-if="isTeamLeader && isActingAsTeamLeader"
          class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white"
          aria-hidden="true"
        />
      </div>
      <div v-show="!collapsed" class="min-w-0 duration-1000 transition-all">
        <p class="text-sm font-semibold text-untitled-gray-800 truncate">
          {{ user.name }}
        </p>
        <span
          v-if="roles"
          class="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 truncate"
        >
          {{ roles }}
        </span>
      </div>
    </div>

    <div
      v-if="isTeamLeader"
      class="flex items-center justify-between gap-3 rounded-lg bg-untitled-gray-50 px-3 py-2"
    >
      <div class="min-w-0">
        <p class="text-xs font-medium text-untitled-gray-700">
          Team Leader Mode
        </p>
        <p class="text-[11px] text-untitled-gray-500 truncate">
          View your team's progress
        </p>
      </div>
      <ElSwitch
        class="switch-as-role shrink-0"
        v-model="isActingAsTeamLeader"
        @change="switchAsTeamLeader"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
